'use client';

import {
    isSilencedDatadogNetworkEvent,
    isSilencedExternalRuntimeNoiseConsoleError,
    isSilencedExternalRuntimeNoiseError,
    isSilencedNextRscConsoleError,
    isSilencedOidcDatadogEvent,
} from '@/app/_shared/lib/error-silencing';
import type { DatadogLogs } from '@datadog/browser-logs';
import type { DatadogRum, User as DatadogRumUser } from '@datadog/browser-rum';

type DatadogSdk = {
    logs: DatadogLogs;
    rum: DatadogRum;
};

const shouldDropDatadogEvent = (event: unknown, context?: unknown): boolean =>
    isSilencedDatadogNetworkEvent(event, context) ||
    isSilencedExternalRuntimeNoiseError(event) ||
    isSilencedOidcDatadogEvent(event);

declare global {
    interface Window {
        __bunqerConsoleErrorPatched?: boolean;
        __bunqerDatadogInitPromise?: Promise<void>;
        __bunqerDatadogInitialized?: boolean;
        __bunqerDatadogSdkPromise?: Promise<DatadogSdk | null>;
        __bunqerErrorSilencingListenersAttached?: boolean;
    }
}

const hasDatadogConfig = (): boolean =>
    Boolean(process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN && process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID);

const attachErrorSilencing = () => {
    if (typeof window === 'undefined' || !hasDatadogConfig()) {
        return;
    }

    if (!window.__bunqerConsoleErrorPatched) {
        const originalConsoleError = console.error;
        console.error = (...args) => {
            if (isSilencedNextRscConsoleError(args) || isSilencedExternalRuntimeNoiseConsoleError(args)) {
                return;
            }

            originalConsoleError(...args);
        };
        window.__bunqerConsoleErrorPatched = true;
    }

    if (!window.__bunqerErrorSilencingListenersAttached) {
        window.addEventListener('unhandledrejection', (event) => {
            if (isSilencedExternalRuntimeNoiseError(event.reason)) {
                event.preventDefault();
            }
        });

        window.addEventListener('error', (event) => {
            const errorCandidate = event.error ?? event.message;
            if (isSilencedExternalRuntimeNoiseError(errorCandidate)) {
                event.preventDefault();
            }
        });

        window.__bunqerErrorSilencingListenersAttached = true;
    }
};

const loadDatadogSdk = (): Promise<DatadogSdk | null> => {
    if (typeof window === 'undefined' || !hasDatadogConfig()) {
        return Promise.resolve(null);
    }

    if (!window.__bunqerDatadogSdkPromise) {
        window.__bunqerDatadogSdkPromise = Promise.all([
            import('@datadog/browser-logs'),
            import('@datadog/browser-rum'),
        ])
            .then(([logsModule, rumModule]) => ({
                logs: logsModule.datadogLogs,
                rum: rumModule.datadogRum,
            }))
            .catch((error) => {
                window.__bunqerDatadogSdkPromise = undefined;
                throw error;
            });
    }

    return window.__bunqerDatadogSdkPromise;
};

export const initDatadog = (): Promise<void> => {
    if (typeof window === 'undefined' || !hasDatadogConfig()) {
        return Promise.resolve();
    }

    attachErrorSilencing();

    if (window.__bunqerDatadogInitialized) {
        return Promise.resolve();
    }

    if (!window.__bunqerDatadogInitPromise) {
        window.__bunqerDatadogInitPromise = loadDatadogSdk()
            .then((sdk) => {
                if (!sdk || window.__bunqerDatadogInitialized) {
                    return;
                }

                sdk.logs.init({
                    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
                    site: 'datadoghq.eu',
                    service: 'frontend',
                    env: process.env.NEXT_PUBLIC_ENV || 'production',
                    version: process.env.NEXT_PUBLIC_APP_VERSION || '0.0.0',
                    forwardErrorsToLogs: true,
                    forwardConsoleLogs: ['error', 'warn'],
                    forwardReports: ['intervention'],
                    useSecureSessionCookie: true,
                    usePartitionedCrossSiteSessionCookie: true,
                    sessionPersistence: 'local-storage',
                    beforeSend: (event: unknown, context: unknown) => !shouldDropDatadogEvent(event, context),
                });

                sdk.rum.init({
                    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || '',
                    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
                    site: 'datadoghq.eu',
                    service: 'frontend',
                    env: process.env.NEXT_PUBLIC_ENV || 'production',
                    version: process.env.NEXT_PUBLIC_APP_VERSION || '0.0.0',
                    sessionSampleRate: 100,
                    sessionReplaySampleRate: 100,
                    defaultPrivacyLevel: 'allow',
                    trackResources: true,
                    trackResourceHeaders: true,
                    trackLongTasks: true,
                    trackUserInteractions: true,
                    compressIntakeRequests: true,
                    allowedTracingUrls: [
                        {
                            match: (url: string) => url.startsWith(process.env.NEXT_PUBLIC_API_URL || ''),
                            propagatorTypes: ['tracecontext', 'datadog'],
                        },
                    ],
                    traceSampleRate: 100,
                    traceContextInjection: 'sampled',
                    allowedGraphQlUrls: [
                        {
                            match: (url: string) => url.startsWith(process.env.NEXT_PUBLIC_API_URL || ''),
                            trackPayload: true,
                            trackResponseErrors: true,
                        },
                    ],
                    useSecureSessionCookie: true,
                    usePartitionedCrossSiteSessionCookie: true,
                    sessionPersistence: 'local-storage',
                    beforeSend: (event: unknown, context: unknown) => !shouldDropDatadogEvent(event, context),
                });

                sdk.rum.startSessionReplayRecording();
                window.__bunqerDatadogInitialized = true;
            })
            .finally(() => {
                window.__bunqerDatadogInitPromise = undefined;
            });
    }

    return window.__bunqerDatadogInitPromise ?? Promise.resolve();
};

export const clearDatadogUser = async () => {
    await initDatadog();

    const sdk = await loadDatadogSdk();
    sdk?.rum.clearUser();
};

export const setDatadogUser = async (user: DatadogRumUser) => {
    await initDatadog();

    const sdk = await loadDatadogSdk();
    sdk?.rum.setUser(user);
};
